import { buildFeatureViewRoutes, createFeatureViewRouter } from "@price-rescue/ui-components";
import type { DefineComponent } from "vue";
import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";

import { ROUTES, canEnterRoute, routeDefinitionForNavigation } from "../navigation.js";
import { useMerchantSessionStore } from "../stores/session-store.js";
import { merchantRouteMetadata } from "./merchant-route-metadata.js";

const viewComponents = import.meta.glob<DefineComponent>("../views/**/*.vue");

export const merchantVueRoutes = [
  ...buildFeatureViewRoutes({
    defaultPath: "/dashboard",
    missingViewLabel: "merchant",
    viewComponents,
    viewRoutes: merchantRouteMetadata,
    toMeta: (route) => ({
      title: route.title,
      navId: route.navId,
    }),
  }),
  ...semanticMerchantRoutes(),
];

export const router = createFeatureViewRouter(merchantVueRoutes);

router.beforeEach((to) => {
  if (isHostedPricingReturn(to)) {
    removeHostedPricingReturnParameters();
    return { name: "plans-pricing", query: { hostedPricingReturn: "1" } };
  }

  const session = useMerchantSessionStore();
  if (!session.hasResolvedSession) return true;

  if (session.status === "reauthorization-required") {
    return to.name === "platform-reauthorization" ? true : { name: "platform-reauthorization" };
  }

  if (["access-pending", "suspended", "revoked"].includes(session.status)) {
    return to.name === "access-pending" ? true : { name: "access-pending" };
  }

  const route = routeDefinitionForNavigation(to.meta["navId"]);
  if (route === undefined || session.sessionContext === undefined) return true;

  return canEnterRoute(route, session.sessionContext) ? true : { name: "dashboard" };
});

function isHostedPricingReturn(to: Pick<RouteLocationNormalized, "name" | "query">): boolean {
  if (typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).has("plan_handle") ||
    Object.hasOwn(to.query, "plan_handle")
  );
}

function removeHostedPricingReturnParameters(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete("plan_handle");
  url.searchParams.delete("charge_id");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

function semanticMerchantRoutes(): RouteRecordRaw[] {
  return [
    { path: "/competitors", redirect: "/competitors-list" },
    { path: "/requests", redirect: "/requests-list" },
    {
      path: "/requests/review-queue",
      redirect: { path: "/requests-list", query: { status: "REVIEW_REQUIRED", sort: "sla" } },
    },
    semanticRoute(
      "/requests/:requestId/review",
      "request-detail-review",
      "../views/requests/detail/request-detail-review.vue",
      "requests",
      "Request review",
      ["/request-detail-review/:requestId"],
    ),
    semanticRoute(
      "/products/:productId",
      "product-detail",
      "../views/products/detail/product-detail.vue",
      "products",
      "Product detail",
      ["/product-detail/:productId"],
    ),
    semanticRoute(
      "/competitors/:competitorId",
      "competitor-detail",
      "../views/competitors/detail/competitor-detail.vue",
      "competitors",
      "Competitor detail",
      ["/competitor-detail/:competitorId"],
    ),
    settingsLegacyRedirect("/settings-store-platform", "widget"),
    settingsLegacyRedirect("/settings-widget", "widget"),
    settingsLegacyRedirect("/settings-notifications-sla", "notifications"),
  ];
}

function semanticRoute(
  path: string,
  name: string,
  componentPath: string,
  navId: string,
  title: string,
  alias: string[] = [],
): RouteRecordRaw {
  return {
    path,
    name: `${name}-semantic`,
    alias,
    component: requiredViewComponent(componentPath),
    meta: { navId, title },
  };
}

function settingsLegacyRedirect(path: string, tab: string): RouteRecordRaw {
  return {
    path,
    redirect: { name: "settings", query: { tab } },
  };
}

function requiredViewComponent(componentPath: string): NonNullable<RouteRecordRaw["component"]> {
  const component = viewComponents[componentPath];
  if (component === undefined)
    throw new Error(`Missing merchant semantic route component: ${componentPath}`);
  return component;
}
